import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Card, CardHeader, Container, IconButton } from '@mui/material'
import { get } from 'lodash'
import React, { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { useActions } from '../hooks/useActions'
import Context from '../utils/context/Context'
import { getFullName, pathToPerson } from '../utils/helpers'
import DialogWindow from './DialogWindow'

export default function PersonsList({ personIds, openModal }) {
  const [isRemoveAlertOpen, setIsRemoveAlertOpen] = useState(false)
  const [personIdToRemove, setPersonIdToRemove] = useState(null)
  const { removePerson } = useActions()
  const { setLoading } = useContext(Context)

  return (
    <Container>
      {personIds.map((id) => (
        <PersonListItem
          key={id}
          id={id}
          openModal={openModal}
          setIsRemoveAlertOpen={setIsRemoveAlertOpen}
          setPersonIdToRemove={setPersonIdToRemove}
        />
      ))}
      <DialogWindow
        open={isRemoveAlertOpen}
        onClose={() => {
          setIsRemoveAlertOpen(false)
        }}
        title="Remove Person"
        text="Are you sure you want to remove this person and all their details?"
        color="warning"
        handleConfirm={confirmRemovePersonEntry}
      />
    </Container>
  )

  /**
   * Removes person from the node list
   */
  function confirmRemovePersonEntry() {
    removePerson({ personId: personIdToRemove })
    setLoading((prevState) => ({
      ...prevState,
      dialog: false,
    }))
    setIsRemoveAlertOpen(false)
    setPersonIdToRemove(null)
  }
}

function PersonListItem({ id, openModal, setIsRemoveAlertOpen, setPersonIdToRemove }) {
  const person = useSelector((state) => get(state, [...pathToPerson(id)]))
  if (!person) {
    return null
  }

  const { sex, relationshipToProband, name } = person

  return (
    <Card elevation={2} sx={{ borderRadius: 2, cursor: 'pointer' }} onClick={() => openModal(id)}>
      <CardHeader
        sx={{
          backgroundColor: (theme) => theme.palette.white.main,
          p: 2,
        }}
        avatar={<AccountCircleIcon fontSize="large" color="primary" />}
        title={getFullName(name?.firstName, name?.lastName)}
        subheader={relationshipLabel()}
        action={
          <IconButton color="error" onClick={handleRemovePerson}>
            <DeleteForeverIcon />
          </IconButton>
        }
      />
    </Card>
  )
  /**
   * Opens dialog to remove person
   */
  function handleRemovePerson(e) {
    e.stopPropagation()
    setIsRemoveAlertOpen(true)
    setPersonIdToRemove(id)
  }
  /**
   * Relationship label for the person
   * @returns {String} - A readable relationship ie. Patient's Sister
   */
  function relationshipLabel() {
    const person = () => {
      if (relationshipToProband.includes('Maternal')) {
        return `Mother's`
      } else if (relationshipToProband.includes('Paternal')) {
        return `Father's`
      } else {
        return `Patient's`
      }
    }
    const relationship = () => {
      const trimmedRelationship = relationshipToProband
        .replace('probands', '')
        .replace('Maternal', '')
        .replace('Paternal', '')
        .replace('Half', '')
        .trim()
      const isSibling = ['Sibling', 'Sister', 'Brother', 'Aunt', 'Uncle'].some((term) =>
        trimmedRelationship.includes(term),
      )

      if (sex === 'F') {
        return isSibling ? 'Sister' : 'Daughter'
      } else if (sex === 'M') {
        return isSibling ? 'Brother' : 'Son'
      }

      return trimmedRelationship
    }
    const isHalfSibling = [
      'HalfSibling',
      'HalfSister',
      'HalfBrother',
      'HalfAunt',
      'HalfUncle',
    ].some((term) => relationshipToProband.includes(term))

    return person() + ' ' + (isHalfSibling ? 'Half-' : '') + relationship()
  }
}
